import type { ContourLabel } from '@/src/store/contours/contour-definitions';

import { useActiveStudyStore } from '@/src/store/active-study';
import { useContourContainerStore } from '@/src/store/contours/contour-container-store';
import { encodeContourLabelItemId } from '@/src/store/contours/contour-definitions';
import { ViewportToolMode } from '@/src/store/contours/types/tool-modes-type';
import { useViewportStateStore } from '@/src/store/contours/viewport-state-store';
import { useCurrentStudyStore } from '@/src/store/dicom-web/current-study-store';
import { SliceObject } from '@/src/store/slice-obj-class';

export function useViewportActions() {
  const contourContainerStore = useContourContainerStore();
  const viewportStateStore = useViewportStateStore();
  const currentStudyStore = useCurrentStudyStore();
  const activeStore = useActiveStudyStore();

  function getStudyObjectByViewerId(viewerID: string) {
    const indices = viewportStateStore.getSelectedImageIndices(viewerID);
    const seriesIdxAsString = String(indices.seriesIdx);
    if (!seriesIdxAsString) {
      return null;
    }
    return activeStore.getStudyObject(
      seriesIdxAsString,
      indices.sliceIdx,
    ) as SliceObject;
  }

  function getStudyObjectByImageUID(imageUID: string) {
    // First get the indexes for the imageUID
    const indices = currentStudyStore.getIndexByImageUID(imageUID);
    if (!indices) {
      return null;
    }
    const { imageSeriesIndex, imageSliceIndex } = indices;
    if (imageSeriesIndex === undefined || imageSliceIndex === undefined) {
      return null;
    }
    const seriesIdxAsString = String(imageSeriesIndex);
    if (!seriesIdxAsString) {
      return null;
    }
    return activeStore.getStudyObject(seriesIdxAsString, imageSliceIndex);
  }

  function pasteContours(imageUID: string) {
    contourContainerStore.pasteTempCopyContours(imageUID);
  }

  function copyImageDrawingContours(imageUID: string, label?: ContourLabel) {
    contourContainerStore.addContoursToTempCopy(imageUID, label);
  }

  function imageHasContours(imageUID: string) {
    return contourContainerStore.hasContours(imageUID);
  }

  function getImageDrawingContours(imageUID: string) {
    return contourContainerStore.getDrawingContours(imageUID);
  }

  function forwardContoursAcrossUIDs(
    imageUID: string,
    targetImageUIDs: string[],
    label?: ContourLabel,
  ) {
    contourContainerStore.addContoursToTempCopy(imageUID, label);
    contourContainerStore.pasteTempsCopyContoursMultiple(
      targetImageUIDs,
      imageUID,
    );
  }

  function forwardContourAcrossPhases(
    viewerID: string,
    imageUID: string,
    label: ContourLabel,
  ) {
    const imageUIDsfromStore
      = currentStudyStore.getSliceImageUIDsbyView(viewerID);
    // Remove current imageUID from the list
    const targetImageUIDs = imageUIDsfromStore.filter(
      uid => uid !== imageUID,
    );

    contourContainerStore.addContoursToTempCopy(imageUID, label);
    contourContainerStore.pasteTempsCopyContoursMultiple(
      targetImageUIDs,
      imageUID,
    );
  }

  function clipboardHasContours() {
    return contourContainerStore.getTempCopyContours().length > 0;
  }

  function handleUndo() {
    contourContainerStore.undo();
  }

  function handleRedo() {
    contourContainerStore.redo();
  }

  function hasPast() {
    return contourContainerStore.hasPast;
  }
  function hasFuture() {
    return contourContainerStore.hasFuture;
  }

  function handleDelete(viewId: string) {
    const selectedContour = viewportStateStore.getSelectedContour(viewId);
    const imageId = viewportStateStore.getSelectedImageUID(viewId);

    if (selectedContour) {
      contourContainerStore.deleteContour(imageId, selectedContour);
    }
  }

  function handleDeleteAll(currentImageUID: string) {
    contourContainerStore.deleteAllContours(currentImageUID);
  }

  function handleDeleteAcrossImages(
    imageUIDs: string[],
    targetLabel?: ContourLabel,
  ) {
    if (!targetLabel) {
      contourContainerStore.bulkDeleteAllContours(imageUIDs);
      return;
    }
    const contourId = {
      label: targetLabel,
      id: encodeContourLabelItemId([0]),
    };
    contourContainerStore.bulkDeleteContour(imageUIDs, contourId);
  }

  function handleCopy() {
    contourContainerStore.undo();
  }

  function handlePaste() {
    contourContainerStore.undo();
  }

  // function saveToFile() {
  //   contourContainerStore.saveToFile();
  // }

  // function loadFromFile() {
  //   contourContainerStore.loadFromFile();
  // }

  function setDrawingContour(viewId: string, label: ContourLabel) {
    viewportStateStore.setToolMode(viewId, ViewportToolMode.Contour);
    viewportStateStore.setDrawingContour(viewId, label);
  }

  function toggleGrid(viewId: string) {
    const state = viewportStateStore.viewportStates[viewId].showGrid;
    viewportStateStore.viewportStates[viewId].showGrid = !state;
  }

  function setImageInterpolationMode(
    viewId: string,
    mode: 'nearest' | 'linear' | 'cubic',
  ) {
    viewportStateStore.setImageInterpolationMode(viewId, mode);
  }

  async function showDicomDump(viewId: string) {
    const indices = viewportStateStore.getSelectedImageIndices(viewId);
    const sopInstanceUID = currentStudyStore.getImageUID(indices);
    const data = await currentStudyStore.getInstanceMetadata(sopInstanceUID);

    function formatDicomDump(obj: Record<string, unknown>, indent = 0): string {
      const pad = '  '.repeat(indent);
      return Object.entries(obj)
        .map(([key, value]) => {
          if (Array.isArray(value)) {
            // Array of objects (sequences) — format each item
            if (value.length > 0 && typeof value[0] === 'object') {
              const items = value
                .map(
                  (item, i) =>
                    `${pad}  [${i}]:\n${formatDicomDump(item as Record<string, unknown>, indent + 2)}`,
                )
                .join('\n');
              return `${pad}${key}:\n${items}`;
            }
            return `${pad}${key}: ${value.join(', ')}`;
          }
          if (value && typeof value === 'object') {
            return `${pad}${key}:\n${formatDicomDump(value as Record<string, unknown>, indent + 1)}`;
          }
          return `${pad}${key}: ${value as any}`;
        })
        .join('\n');
    }

    const formattedData = data && formatDicomDump(data);

    // Create popup window with basic styling
    const popup = window.open('', 'DICOM Dump', 'width=600,height=800');
    if (popup) {
      popup.document.write(`
        <html>
          <head>
            <title>DICOM Metadata</title>
            <style>
              body {
                font-family: monospace;
                padding: 20px;
                background: #f5f5f5;
              }
              pre {
                background: white;
                padding: 15px;
                border-radius: 4px;
                box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                overflow: auto;
                max-height: calc(100vh - 80px);
              }
            </style>
          </head>
          <body>
            <h2>DICOM Metadata</h2>
            ${formattedData ? `<pre>${formattedData}</pre>` : 'Could not retrieve metadata'}
          </body>
        </html>
      `);
    }
  }

  function excludeImage(viewerID: string, imageUID: string) {
    const studyObject = getStudyObjectByViewerId(viewerID);
    if (!studyObject) {
      return;
    }
    studyObject.addExcludedImage(imageUID);
  }

  function excludeImages(viewerID: string, imageUIDs: string[]) {
    if (!imageUIDs || imageUIDs.length === 0) return;
    const studyObject = getStudyObjectByViewerId(viewerID);
    if (!studyObject) return;
    imageUIDs.forEach((imageUID) => {
      studyObject.addExcludedImage(imageUID);
    });
  }

  function updateStartEndBreathHold(
    viewerID: string,
    startPhase: number,
    endPhase: number,
  ) {
    const studyObject = getStudyObjectByViewerId(viewerID);
    if (!studyObject) {
      return;
    }
    const breathholdState = studyObject.getBreathhold();
    if (!breathholdState) {
      console.error('Breathhold state not found');
      return;
    }
    breathholdState.updateStartEnd(startPhase, endPhase);
  }

  function setPhaseToCurrent(
    viewerID: string,
    seriesIdx: number,
    sliceIdx: number,
    imageIdx: number,
  ) {
    viewportStateStore.setSelectedImageIndices(viewerID, {
      seriesIdx,
      sliceIdx,
      imageIdx,
    });
  }

  function getStartEndBreathHold(viewerID: string) {
    const studyObject = getStudyObjectByViewerId(viewerID);
    if (!studyObject) {
      return { start: 0, end: 0 };
    }
    const breathholdState = studyObject.getBreathhold();
    if (!breathholdState) {
      return { start: 0, end: 0 };
    }
    return {
      start: breathholdState.getStart(),
      end: breathholdState.getEnd(),
    };
  }

  return {
    setPhaseToCurrent,
    handleUndo,
    handleRedo,
    hasPast,
    hasFuture,
    handleDeleteAcrossImages,
    handleDelete,
    handleDeleteAll,
    handleCopy,
    handlePaste,
    // saveToFile,
    // loadFromFile,
    setDrawingContour,
    toggleGrid,
    setImageInterpolationMode,
    showDicomDump,
    excludeImage,
    excludeImages,
    updateStartEndBreathHold,
    getStartEndBreathHold,
    getStudyObjectByViewerId,
    getStudyObjectByImageUID,
    copyImageDrawingContours,
    pasteContours,
    forwardContourAcrossPhases,
    forwardContoursAcrossUIDs,
    imageHasContours,
    getImageDrawingContours,
    clipboardHasContours,
  };
}
